import { ReleaseHighlight, useCreateReleaseHighlight, useUpdateReleaseHighlight } from "@/entities/release-highlight";
import {
    Button,
    DialogBody,
    DialogFooter,
    ErrorDisplay,
    Form,
    FormControl,
    FormField,
    FormFieldInput,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/shared/components";
import { MarkdownEditor } from "@/shared/components/markdown/markdown-editor.tsx";
import { FormFieldInputDateTime } from "@/shared/components/ui/input-datetime-formfield.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
    titel: z.string().trim().nonempty("Vul een titel in"),
    korteOmschrijving: z.string().trim().nonempty("Vul een korte omschrijving in"),
    datum: z.date("Vul een publicatiedatum en -tijd in"),
    volledigeOmschrijving: z.string().trim().nonempty("Vul de tekst van het bericht in"),
});

export type ReleaseHighlightFormWaarden = z.infer<typeof schema>;

type ReleaseHighlightFormProps = {
    bericht: ReleaseHighlight | null;
    onSaved: () => void;
    onCancel: () => void;
};

export function ReleaseHighlightForm({ bericht, onSaved, onCancel }: Readonly<ReleaseHighlightFormProps>) {
    const createBericht = useCreateReleaseHighlight();
    const updateBericht = useUpdateReleaseHighlight();

    const form = useForm<ReleaseHighlightFormWaarden>({
        resolver: zodResolver(schema),
        defaultValues: {
            titel: bericht?.titel ?? "",
            korteOmschrijving: bericht?.korteOmschrijving ?? "",
            datum: bericht?.datum ?? new Date(),
            volledigeOmschrijving: bericht?.volledigeOmschrijving ?? "",
        },
    });

    const isPending = createBericht.isPending || updateBericht.isPending;

    const onSubmit = (waarden: ReleaseHighlightFormWaarden) => {
        const options = { onSuccess: onSaved };

        if (bericht) {
            updateBericht.mutate({ id: bericht.id, ...ReleaseHighlight.toUpsertDto(waarden) }, options);
        } else {
            createBericht.mutate(ReleaseHighlight.toUpsertDto(waarden), options);
        }
    };

    return (
        <Form {...form}>
            <form
                onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}
                className="grid min-h-0 grid-rows-[minmax(0,1fr)_auto] overflow-hidden"
            >
                <DialogBody className="flex flex-col gap-4 overflow-y-auto">
                    <div className="grid grid-cols-2 items-start gap-2">
                        <FormFieldInput form={form} name="titel" label="Titel" helpText=" " data-testid="release-highlight-titel" />
                        <FormFieldInputDateTime
                            form={form}
                            name="datum"
                            label="Publicatiedatum"
                            helptext="Het bericht is pas zichtbaar voor andere gebruikers na publicatie."
                        />
                    </div>

                    <FormFieldInput
                        form={form}
                        name="korteOmschrijving"
                        label="Korte omschrijving"
                        helpText="Deze regel staat in de lijst onder het nieuwsicoon."
                        data-testid="release-highlight-omschrijving"
                    />
                    <FormField
                        control={form.control}
                        name="volledigeOmschrijving"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Tekst</FormLabel>
                                <FormControl>
                                    <MarkdownEditor
                                        value={field.value ?? ""}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        data-testid="release-highlight-tekst"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </DialogBody>
                <DialogFooter>
                    {createBericht.error && <ErrorDisplay error={createBericht.error} />}
                    {updateBericht.error && <ErrorDisplay error={updateBericht.error} />}
                    <Button type="button" variant="tertiary" onClick={onCancel}>
                        Annuleren
                    </Button>
                    <Button type="submit" disabled={isPending} data-testid="release-highlight-opslaan">
                        Opslaan
                    </Button>
                </DialogFooter>
            </form>
        </Form>
    );
}
